import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../hooks/axios";
import InputWithLabel from "../../components/form/input/InputWithLabel";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import { VehicleTimePriceFormData } from "../../types/vehicleTimePrice";
import Select from "../../components/form/Select";
import { SelectOption } from '../../types/select';

interface VehicleType {
  id: string;
  description: string;
}

export default function VehicleTimePriceForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState<VehicleTimePriceFormData>({
    vehicleTypeId: "",
    fromTime: "",
    toTime: "",
    fromTimeString: "",
    toTimeString: "",
    priceAdjustPers: 0,
    status: true
  });

  const [vehicleTypes, setVehicleTypes] = useState<VehicleType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchVehicleTimePriceData = useCallback(async () => {
    try {
      const response = await axiosInstance.get(`/vehicletimeprices/${id}`);
      const data = response.data;
      setFormData({
        vehicleTypeId: data.vehicleTypeId || "",
        fromTime: data.fromTime || "",
        toTime: data.toTime || "",
        fromTimeString: data.fromTimeString || "",
        toTimeString: data.toTimeString || "",
        priceAdjustPers: data.priceAdjustPers || 0,
        status: data.status ?? true
      });
    } catch (error) {
      setError("Failed to fetch vehicle time price data");
      console.error("Error fetching vehicle time price:", error);
    }
  }, [id]);

  const fetchVehicleTypes = useCallback(async () => {
    try {
      const response = await axiosInstance.get("/vehicletypes");
      setVehicleTypes(response.data);
    } catch (error) {
      console.error("Error fetching vehicle types:", error);
      setError("Failed to fetch vehicle types");
    }
  }, []);

  useEffect(() => {
    fetchVehicleTypes();
    if (isEditMode) {
      fetchVehicleTimePriceData();
    }
  }, [isEditMode, fetchVehicleTypes, fetchVehicleTimePriceData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      if (isEditMode) {
        await axiosInstance.put(`/vehicletimeprices/${id}`, formData);
      } else {
        await axiosInstance.post("/vehicletimeprices", formData);
      }
      navigate("/vehicle-time-prices");
    } catch (error: any) {
      setError(error.response?.data?.message || "Failed to save time price");
      console.error("Error saving time price:", error);
    } finally {
      setLoading(false);
    }
  };

  const isNextDay = (fromTime: string, toTime: string): boolean => {
    try {
      const [fromHours, fromMinutes] = fromTime.split(':').map(Number);
      const [toHours, toMinutes] = toTime.split(':').map(Number);
      
      // Convert to minutes for easier comparison
      const fromTotalMinutes = fromHours * 60 + fromMinutes;
      const toTotalMinutes = toHours * 60 + toMinutes;
      
      // If toTime is less than or equal to fromTime, it's next day
      return toTotalMinutes <= fromTotalMinutes;
    } catch (error) {
      return false;
    }
  };

  const convertTimeToString = (time: string, isToTime: boolean = false, compareTime?: string): string => {
    try {
      const [hours, minutes] = time.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      const timeString = `${hour12}:${minutes} ${ampm}`;
      
      // Add next day indicator if it's toTime and meets next day condition
      if (isToTime && compareTime && isNextDay(compareTime, time)) {
        return `${timeString} (Next Day)`;
      }
      
      return timeString;
    } catch (error) {
      return '';
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    
    setFormData(prev => {
      const updates: Partial<VehicleTimePriceFormData> = {
        [name]: type === 'number' ? parseFloat(value) : value
      };

      if (name === 'fromTime') {
        updates.fromTimeString = convertTimeToString(value);
        // If toTime exists, update its string to reflect potential next day status
        if (prev.toTime) {
          updates.toTimeString = convertTimeToString(prev.toTime, true, value);
        }
      } else if (name === 'toTime') {
        updates.toTimeString = convertTimeToString(value, true, formData.fromTime);
      }

      return { ...prev, ...updates };
    });
  };

  const handleSelectChange = (value: string) => {
    setFormData(prev => ({ ...prev, vehicleTypeId: value }));
  };

  return (
    <div className="w-full space-y-6">
      <PageBreadcrumb pageTitle={isEditMode ? "Edit Time Price" : "Add Time Price"} />

      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03] lg:p-6">
        {error && (
          <div className="mb-4 rounded-xl border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700 dark:border-error-500/30 dark:bg-error-500/10 dark:text-error-400">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="w-full space-y-6">
          <div className="grid w-full grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Vehicle Type
              </label>
              <Select
                label="Vehicle Type"
                name="vehicleTypeId"
                placeholder="Select Vehicle Type"
                value={formData.vehicleTypeId}
                onChange={handleSelectChange}
                options={vehicleTypes.map((type): SelectOption => ({
                  value: type.id,
                  label: type.description
                }))}
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                From Time
              </label>
              <input
                type="time"
                name="fromTime"
                value={formData.fromTime}
                onChange={handleInputChange}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              {formData.fromTimeString && (
                <div className="text-sm text-gray-500 mt-1">
                  {formData.fromTimeString}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                To Time
              </label>
              <input
                type="time"
                name="toTime"
                value={formData.toTime}
                onChange={handleInputChange}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              {formData.toTimeString && (
                <div className="text-sm text-gray-500 mt-1">
                  {formData.toTimeString}
                </div>
              )}
            </div>

            <InputWithLabel
              label="Price Adjustment (%)"
              name="priceAdjustPers"
              type="number"
              value={formData.priceAdjustPers.toString()}
              onChange={handleInputChange}
              step={0.01}
              required
            />

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Status
              </label>
              <select
                name="status"
                value={formData.status.toString()}
                onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value === 'true' }))}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate("/vehicle-time-prices")}
              className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600 disabled:opacity-50"
            >
              {loading ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 